const defaultLabels = {
  "heading_default": "Your quiz rewards",
  "preview_label": "Rewards design preview",
  "preview_saving": "Preview saving {saving} · Quizzes {total}",
  "actual_total": "Current cart {total}",
  "next_one": "Add one more for {rate}% off.",
  "next_many": "Add {count} more for {rate}% off.",
  "earned": "Preview: {rate}% off your quiz lineup.",
  "top_reward": "Preview: top reward, {rate}% off.",
  "start": "More quiz nights, more rewards.",
  "choose": "Browse more quizzes",
  "view_cart": "View cart",
  "checkout": "Just checkout",
  "keep_shopping": "Keep shopping",
  "selected": "{count} of 10 quizzes selected",
  "off": "{rate}% off",
  "reward_label": "{count}-quiz reward: {rate}% off. {status}",
  "locked": "Not unlocked yet",
  "completed": "Milestone reached",
  "current": "Current preview reward",
  "progress_label": "Quizzes in your lineup",
  "announcement": "Preview reward reached: {rate}% off at {count} quizzes. Checkout prices are unchanged.",
  "invalid_settings": "Set increasing quiz counts and discount percentages to preview rewards.",
  "refresh_error": "Rewards are unavailable. Your cart and checkout still work.",
  "actual_test_label": "Automatic quiz rewards test",
  "actual_total_live": "Cart total {total}.",
  "actual_saving": "{saving} saved",
  "actual_earned": "{rate}% OFF!",
  "actual_top_reward": "{rate}% OFF!",
  "actual_next_one": "Add 1 more quiz for {rate}% off",
  "actual_next_many": "Add {count} more quizzes for {rate}% off",
  "actual_confirm": "Check your savings at checkout.",
  "actual_current": "Current reward",
  "actual_announcement": "Reward unlocked: {rate}% off your quizzes.",
  "updating": "Updating rewards…",
  "unlock": "Unlock {rate}% OFF!",
  "preview_earned": "Preview: {rate}% OFF!",
  "preview_saved": "Estimated {saving} saved",
  "minimize": "Minimize quiz rewards",
  "show_rewards": "Show quiz rewards",
  "compact_rewards": "Quiz rewards"
};
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const labelKeys = ['heading_default', 'preview_label', 'preview_saving', 'actual_total', 'next_one', 'next_many', 'earned', 'top_reward', 'start', 'choose', 'view_cart', 'checkout', 'keep_shopping', 'selected', 'off', 'reward_label', 'locked', 'completed', 'current', 'progress_label', 'announcement', 'invalid_settings', 'refresh_error'];
const actualLabelKeys = ['actual_test_label', 'actual_total_live', 'actual_saving', 'actual_earned', 'actual_top_reward', 'actual_next_one', 'actual_next_many', 'actual_confirm', 'actual_current', 'actual_announcement'];
const actualTiers = [{ enabled: true, count: 2, rate: 10 }, { enabled: true, count: 3, rate: 15 }, { enabled: true, count: 10, rate: 25 }];
const rewardTitle = rate => `Quiz rewards — ${rate}%`;
const ownedTitles = new Set(actualTiers.map(tier => rewardTitle(tier.rate)));
const integer = value => Number.isSafeInteger(value) && value >= 0;
const firstAttentionKey = 'qr-rewards-first-attention-v1';
let firstAttentionClaimed = false;
const stickyPreferenceKey = 'qr-rewards-sticky-display-v1';
let rewardPanelSequence = 0;

function readStickyPreference() {
  try { return window.sessionStorage.getItem(stickyPreferenceKey) === 'collapsed'; }
  catch { return false; }
}

function visibleSurface(node) {
  if (!node || node.hidden || node.getAttribute('aria-hidden') === 'true' || !node.getClientRects().length) return false;
  const rect = node.getBoundingClientRect();
  const style = getComputedStyle(node);
  return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && style.visibility !== 'hidden' && style.display !== 'none';
}

function claimFirstAttention() {
  if (firstAttentionClaimed) return false;
  try {
    if (window.sessionStorage.getItem(firstAttentionKey)) {
      firstAttentionClaimed = true;
      return false;
    }
    window.sessionStorage.setItem(firstAttentionKey, 'shown');
    firstAttentionClaimed = true;
    return true;
  } catch {
    return false;
  }
}

export function interpolate(template, values = {}) {
  return String(template ?? '').replace(/\{(count|rate|saving|total|status)\}/g, (match, key) => values[key] === undefined ? match : String(values[key]));
}

export function milestonePosition(count) {
  const value = Math.max(1, Math.min(10, Number(count) || 1));
  return value <= 3 ? (value - 1) * 18 : 36 + (value - 3) * 64 / 7;
}

export function shoppingAction(context, pathname = '/') {
  if (context === 'drawer') return 'dismiss';
  if (pathname.includes('/products/')) return 'browse';
  return context === 'sticky' && pathname.includes('/collections/') ? 'cart' : 'browse';
}

export function normalizeConfig(value) {
  if (!value || value.enabled !== true || !['both', 'sticky', 'cart'].includes(value.placement) || typeof value.preview !== 'boolean') return null;
  if (!labelKeys.every(key => typeof value.labels?.[key] === 'string' && value.labels[key].trim())) return null;
  if (value.actual === true && value.preview) return null;
  const actual = value.actual === true;
  const publicMode = value.public === true;
  if (publicMode && (!actual || value.preview)) return null;
  if (actual && !actualLabelKeys.every(key => typeof value.labels?.[key] === 'string' && value.labels[key].trim())) return null;
  const actualProductIds = actual && Array.isArray(value.actualProductIds) ? value.actualProductIds.map(id => String(id)) : [];
  if (actual && !publicMode && (!actualProductIds.length || !actualProductIds.every(id => /^[1-9]\d*$/.test(id)) || new Set(actualProductIds).size !== actualProductIds.length)) return null;
  const tierValues = actual ? actualTiers : value.tiers;
  if (!Array.isArray(tierValues) || tierValues.length !== 3) return null;
  const tiers = tierValues.map((tier, index) => ({ count: tier.count, rate: tier.rate, enabled: tier.enabled === true, index }));
  const active = tiers.filter(tier => tier.enabled);
  if (!active.length || !active.every(tier => integer(tier.count) && tier.count >= 1 && tier.count <= 10 && integer(tier.rate) && tier.rate > 0 && tier.rate < 100)) return null;
  if (active.some((tier, index) => index > 0 && (tier.count <= active[index - 1].count || tier.rate <= active[index - 1].rate))) return null;
  return { ...value, actual, public: publicMode, actualProductIds, heading: String(value.heading || value.labels.heading_default), tiers, active };
}

export function normalizeCart(value) {
  if (!value || !integer(value.itemCount) || !integer(value.total) || !/^[A-Z]{3}$/.test(value.currency) || !Array.isArray(value.items)) return null;
  const items = [];
  for (const item of value.items) {
    if (!item || !integer(item.quantity) || item.quantity < 1 || !integer(item.originalLinePrice) || !integer(item.finalLinePrice) || item.finalLinePrice > item.originalLinePrice || typeof item.eligible !== 'boolean') return null;
    const productId = String(item.productId ?? '');
    const variantId = String(item.variantId ?? '');
    if (!/^\d+$/.test(productId) || !/^\d+$/.test(variantId)) return null;
    let discountAllocations = null;
    if (item.discountAllocations !== undefined) {
      if (!Array.isArray(item.discountAllocations)) return null;
      discountAllocations = [];
      for (const allocation of item.discountAllocations) {
        if (!allocation || !integer(allocation.amount) || typeof allocation.title !== 'string' || typeof allocation.valueType !== 'string') return null;
        const type = allocation.type == null ? '' : allocation.type;
        if (typeof type !== 'string') return null;
        const value = typeof allocation.value === 'number' ? allocation.value : typeof allocation.value === 'string' && /^\d+(?:\.\d+)?$/.test(allocation.value) ? Number(allocation.value) : NaN;
        if (!Number.isFinite(value) || value < 0) return null;
        discountAllocations.push({ amount: allocation.amount, title: allocation.title, type, valueType: allocation.valueType, value });
      }
      const totalAllocation = discountAllocations.reduce((sum, allocation) => sum + allocation.amount, 0);
      if (!integer(totalAllocation) || totalAllocation !== item.originalLinePrice - item.finalLinePrice) return null;
    }
    items.push({ ...item, productId, variantId, discountAllocations });
  }
  if (items.reduce((sum, item) => sum + item.quantity, 0) !== value.itemCount) return null;
  const eligible = items.filter(item => item.eligible);
  const productIds = [...new Set(eligible.map(item => item.productId))];
  const eligibleOriginal = eligible.reduce((sum, item) => sum + item.originalLinePrice, 0);
  const eligibleQuantity = eligible.reduce((sum, item) => sum + item.quantity, 0);
  if (!integer(eligibleOriginal) || !integer(eligibleQuantity)) return null;
  return { ...value, items, productIds, count: productIds.length, eligibleOriginal, eligibleQuantity };
}

export function rewardModel(cart, config) {
  const eligible = config.actual ? cart.items.filter(item => item.eligible && (config.public || config.actualProductIds.includes(item.productId))) : [];
  const count = config.actual ? new Set(eligible.map(item => item.productId)).size : cart.count;
  const qualified = config.active.filter(tier => count >= tier.count).at(-1) ?? null;
  const next = config.active.find(tier => tier.count > count) ?? null;
  let reached = qualified;
  let saving = config.preview ? Math.round(cart.eligibleOriginal * (reached?.rate ?? 0) / 100) : null;
  let estimate = saving === null ? null : cart.eligibleOriginal - saving;
  if (config.actual) {
    const owned = qualified && cart.items.every(item => {
      if (!item.discountAllocations) return false;
      const matches = item.discountAllocations.filter(allocation => ownedTitles.has(allocation.title));
      if (!eligible.includes(item)) return matches.length === 0;
      if (matches.length !== 1 || item.discountAllocations.length !== 1) return false;
      const allocation = matches[0];
      const percentage = BigInt(item.originalLinePrice) * BigInt(qualified.rate);
      const minimum = Number(percentage / 100n);
      const maximum = minimum + Number(percentage % 100n > 0n);
      const missingTypeMatchesAmount = allocation.type === '' && allocation.amount >= minimum && allocation.amount <= maximum;
      return allocation.title === rewardTitle(qualified.rate) && (allocation.type === 'automatic' || missingTypeMatchesAmount) && allocation.valueType === 'percentage' && allocation.value === qualified.rate && allocation.amount > 0;
    });
    reached = owned ? qualified : null;
    saving = owned ? eligible.reduce((sum, item) => sum + item.discountAllocations[0].amount, 0) : null;
    estimate = owned ? eligible.reduce((sum, item) => sum + item.finalLinePrice, 0) : null;
  }
  return { count, qualified, reached, next, gap: next ? next.count - count : 0, saving, estimate, confirmed: Boolean(config.actual && reached) };
}

export function formatMoney(cents, currency, locale) {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  }
}

export function crossedMilestone(previous, current, config, successfulAdd) {
  if (!successfulAdd || !config.celebrate) return null;
  if (config.actual) {
    const before = rewardModel(previous, config), after = rewardModel(current, config);
    return after.confirmed && after.reached.index > 0 && after.count > before.count && (!before.confirmed || after.reached.index > before.reached.index) ? after.reached : null;
  }
  if (!config.preview || current.count <= previous.count || !current.productIds.some(id => !previous.productIds.includes(id))) return null;
  return config.active.filter(tier => tier.index > 0 && previous.count < tier.count && current.count >= tier.count).at(-1) ?? null;
}

export function sameOriginUrl(value, base) {
  try {
    const url = new URL(value, base);
    const origin = new URL(base).origin;
    return url.origin === origin && ['http:', 'https:'].includes(url.protocol) ? url : null;
  } catch {
    return null;
  }
}

function readJson(selector, scope = document) {
  const node = scope.querySelector(selector);
  if (!node || node.tagName !== 'SCRIPT' || node.type !== 'application/json') return null;
  try {
    return JSON.parse(node.textContent);
  } catch {
    return null;
  }
}

function readRuntimeConfig() {
  const value = readJson('script[data-qr-rewards-config]');
  return value ? { ...value, labels: { ...defaultLabels, ...value.labels } } : null;
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function createUi(host, config) {
  const card = element('section', 'qr-rewards__card');
  card.id = `qr-rewards-panel-${++rewardPanelSequence}`;
  card.setAttribute('aria-label', config.heading);
  const preview = element('p', 'qr-rewards__preview', config.actual ? config.labels.actual_test_label : config.labels.preview_label);
  preview.hidden = config.public || !config.preview && !config.actual;
  const layout = element('div', 'qr-rewards__layout');
  const count = element('span', 'qr-rewards__count');
  count.hidden = true;
  count.setAttribute('aria-hidden', 'true');
  const message = element('div', 'qr-rewards__message');
  const headline = element('h2', 'qr-rewards__headline');
  const saved = element('p', 'qr-rewards__saved');
  saved.hidden = true;
  message.append(headline, saved);
  const estimate = element('p', 'qr-rewards__estimate');
  const actual = element('p', 'qr-rewards__actual');
  actual.hidden = true;
  const journey = element('div', 'qr-rewards__journey');
  const awards = element('div', 'qr-rewards__awards');
  const markerCounts = new Set([1, 10, ...config.active.map(tier => tier.count)]);
  const positions = config.active.map(tier => milestonePosition(tier.count));
  const crowded = positions.some((position, index) => index > 0 && position - positions[index - 1] < 15);
  awards.dataset.crowded = String(crowded);
  const badges = new Map();
  for (const [activeIndex, tier] of config.active.entries()) {
    const badge = element('div', `qr-rewards__badge qr-rewards__badge--${tier.index === 2 ? 'gold' : tier.index === 0 ? 'first' : 'seal'}`);
    badge.style.setProperty('--qr-milestone-position', `${milestonePosition(tier.count)}%`);
    badge.dataset.row = String(crowded ? activeIndex % 2 : 0);
    badge.setAttribute('role', 'img');
    const art = element('div', 'qr-rewards__badge-art');
    art.setAttribute('aria-hidden', 'true');
    const percentage = element('strong', 'qr-rewards__badge-percent', `${tier.rate}%`);
    art.append(percentage);
    badge.append(art);
    awards.append(badge);
    badges.set(tier.index, badge);
  }
  const middle = config.active.find(tier => tier.index === 1);
  const last = config.active.find(tier => tier.index === 2);
  const copyStart = middle ? milestonePosition(middle.count) : 36;
  const copyEnd = last ? milestonePosition(last.count) : 100;
  const tight = crowded || copyEnd - copyStart < 40;
  awards.dataset.tight = String(tight);
  awards.style.setProperty('--qr-copy-start', `${copyStart}%`);
  awards.style.setProperty('--qr-copy-end', `${copyEnd}%`);
  estimate.dataset.tight = String(tight);
  const track = element('div', 'qr-rewards__track');
  track.setAttribute('role', 'progressbar');
  track.setAttribute('aria-label', config.labels.progress_label);
  track.setAttribute('aria-valuemin', '0');
  track.setAttribute('aria-valuemax', '10');
  const numbers = element('div', 'qr-rewards__numbers');
  numbers.style.setProperty('--qr-copy-start', `${copyStart}%`);
  numbers.style.setProperty('--qr-copy-end', `${copyEnd}%`);
  const steps = [];
  const numberNodes = [];
  for (let step = 1; step <= 10; step++) {
    const segment = element('span', 'qr-rewards__segment');
    segment.setAttribute('aria-hidden', 'true');
    segment.style.setProperty('--qr-milestone-position', `${milestonePosition(step)}%`);
    segment.hidden = !markerCounts.has(step);
    track.append(segment);
    steps.push(segment);
    const number = element('span', 'qr-rewards__number');
    number.setAttribute('aria-hidden', 'true');
    number.dataset.step = String(step);
    number.style.setProperty('--qr-milestone-position', `${milestonePosition(step)}%`);
    number.hidden = !markerCounts.has(step);
    number.append(element('strong', '', String(step)));
    const milestone = config.active.find(tier => tier.count === step);
    if (milestone) {
      number.dataset.milestone = String(milestone.index);
    }
    numbers.append(number);
    numberNodes.push(number);
  }
  numbers.append(estimate);
  journey.append(awards, track, numbers);
  const actions = element('div', 'qr-rewards__actions');
  const mode = shoppingAction(host.dataset.context, location.pathname || '/');
  const keep = element('button', 'qr-rewards__browse qr-rewards__keep', config.labels.keep_shopping);
  keep.type = 'button';
  keep.dataset.qrRewardsKeepShopping = '';
  keep.hidden = mode !== 'dismiss';
  const browse = element('a', 'qr-rewards__browse', config.labels.choose);
  browse.href = config.browseUrl;
  browse.hidden = mode !== 'browse';
  const view = element('a', 'qr-rewards__view', config.labels.view_cart);
  view.href = config.cartUrl;
  view.dataset.qrRewardsViewCart = '';
  view.hidden = mode !== 'cart';
  if (mode === 'cart') view.className += ' qr-rewards__browse';
  const checkout = element('a', 'qr-rewards__checkout', config.labels.checkout);
  checkout.href = config.checkoutUrl;
  actions.append(keep, browse, view);
  if (host.dataset.context !== 'drawer') actions.append(checkout);
  const status = element('p', 'qr-rewards__status');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'off');
  status.setAttribute('aria-atomic', 'true');
  layout.append(message, journey, actions);
  card.append(preview, layout, count, actual, status);
  let minimize = null;
  let reopen = null;
  if (host.dataset.context === 'sticky') {
    minimize = element('button', 'qr-rewards__minimize', '−');
    minimize.type = 'button';
    minimize.dataset.qrRewardsMinimize = '';
    minimize.setAttribute('aria-label', config.labels.minimize);
    minimize.setAttribute('aria-controls', card.id);
    minimize.setAttribute('aria-expanded', 'true');
    card.append(minimize);
    reopen = element('button', 'qr-rewards__reopen', config.labels.compact_rewards);
    reopen.type = 'button';
    reopen.dataset.qrRewardsExpand = '';
    reopen.setAttribute('aria-label', config.labels.show_rewards);
    reopen.setAttribute('aria-controls', card.id);
    reopen.setAttribute('aria-expanded', 'false');
    reopen.hidden = true;
  }
  host.replaceChildren(card, ...(reopen ? [reopen] : []));
  return { card, count, headline, saved, estimate, actual, track, steps, numberNodes, badges, status, minimize, reopen };
}

class RewardsController {
  constructor(config) {
    this.config = { ...config };
    for (const [key, fallback] of [['browseUrl', '/collections/single-quiz-packs'], ['cartUrl', '/cart'], ['checkoutUrl', '/checkout']]) {
      this.config[key] = sameOriginUrl(config[key], location.href)?.href ?? new URL(fallback, location.href).href;
    }
    this.cart = normalizeCart(readJson('script[data-qr-rewards-cart]'));
    this.valid = false;
    this.updating = false;
    this.initialized = false;
    this.widgets = new Map();
    this.epoch = 0;
    this.pending = 0;
    this.dirty = false;
    this.addSucceeded = false;
    this.running = false;
    this.abort = new AbortController();
    this.stickyCollapsed = readStickyPreference();
    this.mobile = matchMedia('(max-width: 749px)');
    this.purchaseEntryVisible = false;
    this.floatingState = '';
    this.floatingObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(this.scheduleVisibility) : null;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.modals = new WeakSet();
    this.modalObserver = new MutationObserver(() => this.scheduleVisibility());
    this.overlayObserver = new MutationObserver(records => {
      const selector = 'dialog, theme-drawer, [aria-modal="true"], #shopify-pc__banner, #zipchat-iframe, #PBarNextFrame';
      for (const record of records) for (const node of [...record.addedNodes, ...record.removedNodes]) {
        if (!(node instanceof Element)) continue;
        const overlays = [...node.querySelectorAll(selector)];
        if (node.matches(selector)) overlays.push(node);
        for (const overlay of overlays) this.observeModal(overlay);
        if (overlays.length) this.scheduleVisibility();
      }
    });
    this.attentionIntersections = new WeakMap();
    this.attentionObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      for (const entry of entries) this.attentionIntersections.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
      this.scheduleVisibility();
    }, { threshold: .25 }) : null;
    const { signal } = this.abort;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartEvent, { signal });
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.onCartEvent, { signal });
    document.addEventListener('theme-drawer:open', this.scheduleVisibility, { signal });
    document.addEventListener('theme-drawer:close', this.scheduleVisibility, { signal });
    document.addEventListener('dialog:open', this.scheduleVisibility, { signal });
    document.addEventListener('dialog:close', this.scheduleVisibility, { signal });
    document.addEventListener('toggle', this.onToggle, { capture: true, signal });
    document.addEventListener('beforetoggle', this.onToggle, { capture: true, signal });
    document.addEventListener('shopify:section:load', this.onSection, { signal });
    document.addEventListener('shopify:section:unload', this.scheduleVisibility, { signal });
    document.addEventListener('cart-section:restored', this.onRestore, { signal });
    document.addEventListener('visibilitychange', this.onVisibility, { signal });
    document.addEventListener('qr-rewards:expand', this.onExpand, { signal });
    document.addEventListener('qr-mobile-purchase:visibility', this.onPurchaseVisibility, { signal });
    document.addEventListener('focusin', this.scheduleVisibility, { signal });
    document.addEventListener('focusout', this.scheduleVisibility, { signal });
    window.addEventListener('resize', this.scheduleVisibility, { signal });
    window.visualViewport?.addEventListener('resize', this.scheduleVisibility, { signal });
    this.mobile.addEventListener('change', this.scheduleVisibility, { signal });
    window.addEventListener('pageshow', this.onPageShow, { signal });
    this.motion.addEventListener('change', this.clearCelebration, { signal });
    this.motion.addEventListener('change', this.clearAttention, { signal });
    this.observeModals();
    this.overlayObserver.observe(document.body, { childList: true, subtree: true });
  }

  mount(host, rebuild = false) {
    const ui = this.widgets.get(host);
    if (!ui || rebuild && !host.contains(ui.card)) this.widgets.set(host, createUi(host, this.config));
    this.attentionObserver?.observe(host);
    if (host.dataset.context === 'sticky') this.floatingObserver?.observe(host);
    host.dataset.preview = String(this.config.preview);
    this.render(host);
    this.syncVisibility();
    this.start();
  }

  start() {
    if (this.initialized) return;
    this.initialized = true;
    this.requestRefresh(false);
  }

  unmount(host) {
    if (this.attentionHost === host) this.clearAttention();
    this.attentionObserver?.unobserve(host);
    this.floatingObserver?.unobserve(host);
    this.attentionIntersections.delete(host);
    this.widgets.delete(host);
    this.scheduleVisibility();
    if (this.celebratingHost === host) this.clearCelebration();
    if (!this.widgets.size) queueMicrotask(() => {
      if (!this.widgets.size && activeController === this) {
        this.destroy();
        activeController = null;
      }
    });
  }

  render(host) {
    const ui = this.widgets.get(host);
    if (!ui || !this.valid || !this.cart) return;
    const model = rewardModel(this.cart, this.config);
    const { labels } = this.config;
    const money = value => formatMoney(value, this.cart.currency, document.documentElement.lang || undefined);
    ui.count.textContent = `${Math.min(model.count, 10)}${model.count > 10 ? '+' : ''}/10`;
    ui.track.setAttribute('aria-valuenow', String(Math.min(model.count, 10)));
    ui.track.style.setProperty('--qr-rewards-progress', String(milestonePosition(model.count) / 100));
    const milestoneDescriptions = this.config.active.map(tier => interpolate(labels.reward_label, {
      ...tier,
      status: (this.config.preview || model.confirmed) && model.count >= tier.count ? labels.completed : labels.locked
    }));
    ui.track.setAttribute('aria-valuetext', `${interpolate(labels.selected, { count: model.count })}. ${milestoneDescriptions.join(' ')}`);
    for (let index = 0; index < 10; index++) {
      ui.steps[index].dataset.complete = String(index < model.count);
      ui.numberNodes[index].dataset.current = String(index + 1 === Math.min(model.count, 10));
      ui.numberNodes[index].dataset.earned = String((this.config.preview || model.confirmed) && index + 1 <= model.count);
    }
    ui.headline.textContent = labels.actual_confirm || defaultLabels.actual_confirm;
    let savingText = '';
    let nextHeadline = false;
    if (this.config.preview) {
      if (model.reached) {
        ui.headline.textContent = interpolate(labels.preview_earned || defaultLabels.preview_earned, { rate: model.reached.rate });
        savingText = interpolate(labels.preview_saved || defaultLabels.preview_saved, { saving: money(model.saving) });
      } else if (model.next) {
        ui.headline.textContent = interpolate(labels.unlock || defaultLabels.unlock, { rate: model.next.rate });
      }
      if (model.next) {
        ui.headline.textContent = interpolate((model.gap === 1 ? labels.actual_next_one : labels.actual_next_many) || (model.gap === 1 ? defaultLabels.actual_next_one : defaultLabels.actual_next_many), { count: model.gap, rate: model.next.rate });
        nextHeadline = true;
      }
    } else if (this.config.actual) {
      if (model.confirmed) {
        ui.headline.textContent = interpolate(model.next ? labels.actual_earned : labels.actual_top_reward, { rate: model.reached.rate });
        savingText = interpolate(labels.actual_saving, { saving: money(model.saving), total: money(model.estimate) });
        if (model.next) {
          ui.headline.textContent = interpolate(model.gap === 1 ? labels.actual_next_one : labels.actual_next_many, { count: model.gap, rate: model.next.rate });
          nextHeadline = true;
        }
      } else if (!model.qualified && model.next) {
        ui.headline.textContent = interpolate(model.gap === 1 ? labels.actual_next_one : labels.actual_next_many, { count: model.gap, rate: model.next.rate });
        nextHeadline = true;
      }
    }
    ui.headline.dataset.copy = nextHeadline ? 'goal' : this.config.actual && !model.confirmed ? 'status' : 'tier';
    if (ui.saved) {
      ui.saved.textContent = savingText;
      ui.saved.hidden = !savingText;
    }
    ui.estimate.textContent = '';
    ui.estimate.hidden = true;
    ui.actual.textContent = interpolate(this.config.actual ? labels.actual_total_live : labels.actual_total, { total: money(this.cart.total) });
    for (const tier of this.config.active) {
      const badge = ui.badges.get(tier.index);
      const reached = (this.config.preview || model.confirmed) && model.count >= tier.count;
      badge.dataset.earned = String(reached);
      badge.dataset.current = String(reached && model.reached?.index === tier.index);
      badge.setAttribute('aria-label', interpolate(labels.reward_label, { ...tier, status: reached ? model.reached?.index === tier.index ? this.config.actual ? labels.actual_current : labels.current : labels.completed : labels.locked }));
    }
    ui.card.setAttribute('aria-busy', String(Boolean(this.updating)));
    if (this.updating) {
      ui.headline.textContent = labels.updating || defaultLabels.updating;
      if (ui.saved) { ui.saved.style.visibility = 'hidden'; ui.saved.setAttribute('aria-hidden', 'true'); }
      ui.estimate.style.visibility = 'hidden';
      ui.estimate.setAttribute('aria-hidden', 'true');
      ui.actual.textContent = '';
    } else {
      if (ui.saved) { ui.saved.style.visibility = ''; ui.saved.setAttribute('aria-hidden', 'false'); }
      ui.estimate.style.visibility = '';
      ui.estimate.setAttribute('aria-hidden', 'false');
    }
  }

  setUpdating(value) {
    this.updating = value;
    for (const [host, ui] of this.widgets) {
      if (value && this.valid && !ui.pendingLayout) {
        const cardHeight = ui.card.getBoundingClientRect?.().height || 0;
        const headlineHeight = ui.headline.getBoundingClientRect?.().height || 0;
        ui.pendingLayout = {
          cardMinimum: ui.card.style.minBlockSize,
          headlineMinimum: ui.headline.style.minBlockSize,
          headlineDisplay: ui.headline.style.display,
          headlineAlignment: ui.headline.style.placeItems
        };
        if (cardHeight > 0) ui.card.style.minBlockSize = `${cardHeight}px`;
        if (headlineHeight > 0) ui.headline.style.minBlockSize = `${headlineHeight}px`;
        ui.headline.style.display = 'grid';
        ui.headline.style.placeItems = 'center';
      } else if (!value && ui.pendingLayout) {
        ui.card.style.minBlockSize = ui.pendingLayout.cardMinimum || '';
        ui.headline.style.minBlockSize = ui.pendingLayout.headlineMinimum || '';
        ui.headline.style.display = ui.pendingLayout.headlineDisplay || '';
        ui.headline.style.placeItems = ui.pendingLayout.headlineAlignment || '';
        ui.pendingLayout = null;
      }
      this.render(host);
    }
  }

  syncDrawerCount() {
    if (this.cart && this.valid) {
      for (const drawer of document.querySelectorAll('cart-drawer-component')) drawer.dataset.cartItemCount = String(this.cart.itemCount);
    }
  }

  observeModals() {
    for (const node of document.querySelectorAll('dialog, theme-drawer, [aria-modal="true"], #shopify-pc__banner, #zipchat-iframe, #PBarNextFrame')) this.observeModal(node);
  }

  observeModal(node) {
    if (!(node instanceof Element) || this.modals.has(node)) return;
    this.modals.add(node);
    this.modalObserver.observe(node, { attributes: true, attributeFilter: ['open', 'aria-modal', 'aria-hidden', 'hidden', 'class', 'style', 'data-zipchat-visible'] });
  }

  onToggle = event => {
    if (event.target instanceof Element && event.target.matches('dialog, theme-drawer')) {
      this.observeModal(event.target);
      this.scheduleVisibility();
    }
  };

  onSection = () => {
    this.observeModals();
    this.requestRefresh(this.pending > 0 || this.running);
  };

  onRestore = () => this.requestRefresh(this.pending > 0 || this.running);
  onPageShow = event => {
    if (event.persisted) this.requestRefresh(false);
  };
  onVisibility = () => {
    if (document.hidden) this.clearCelebration();
    if (document.hidden) this.clearAttention();
    this.scheduleVisibility();
  };

  onExpand = () => this.setStickyCollapsed(false, true);

  onPurchaseVisibility = event => {
    const visible = event.detail?.visible === true;
    this.purchaseEntryVisible = visible;
    this.scheduleVisibility();
  };

  setStickyCollapsed(collapsed, moveFocus = false) {
    this.stickyCollapsed = Boolean(collapsed);
    try { window.sessionStorage.setItem(stickyPreferenceKey, collapsed ? 'collapsed' : 'expanded'); }
    catch {}
    this.clearAttention();
    this.clearCelebration();
    this.syncVisibility();
    if (!moveFocus) return;
    const sticky = [...this.widgets.keys()].find(host => host.dataset.context === 'sticky');
    const ui = this.widgets.get(sticky);
    const entry = document.querySelector('[data-qrpack-rewards-toggle]');
    const target = collapsed ? visibleSurface(entry) ? entry : ui?.reopen : ui?.minimize;
    if (visibleSurface(target)) target.focus({ preventScroll: true });
  }

  syncFloatingSpace(stickyAllowed) {
    const root = document.documentElement;
    const state = {
      active: stickyAllowed,
      collapsed: this.stickyCollapsed,
      expanded: stickyAllowed && !this.stickyCollapsed
    };
    for (const key of ['active', 'collapsed', 'expanded']) {
      const name = `data-qr-rewards-${key}`;
      if (root.hasAttribute(name) !== state[key]) root.toggleAttribute(name, state[key]);
    }
    const nextState = JSON.stringify(state);
    if (nextState !== this.floatingState) {
      this.floatingState = nextState;
      document.dispatchEvent(new CustomEvent('qr-rewards:visibility', { detail: state }));
    }
    const sticky = [...this.widgets.keys()].find(host => host.dataset.context === 'sticky' && visibleSurface(host));
    const space = sticky ? `${Math.ceil(Math.max(0, innerHeight - sticky.getBoundingClientRect().top))}px` : '0px';
    if (root.style.getPropertyValue('--qr-rewards-reserved-space') !== space) root.style.setProperty('--qr-rewards-reserved-space', space);
  }

  scheduleVisibility = () => {
    if (this.frame !== undefined || this.abort.signal.aborted) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = undefined;
      this.syncVisibility();
    });
  };

  syncVisibility() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    const drawerOpen = Boolean(drawer?.hasAttribute('open'));
    const panel = drawer?.querySelector('dialog');
    const pageWidget = [...this.widgets.keys()].some(host => host.dataset.context === 'page' && host.isConnected);
    const otherModal = [...document.querySelectorAll('dialog:modal, [aria-modal="true"]')].some(node => {
      if (node === panel || node.hidden) return false;
      if (node instanceof HTMLDialogElement) return node.open;
      const rect = node.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.bottom > 0 && rect.left < innerWidth && rect.top < innerHeight && getComputedStyle(node).visibility !== 'hidden';
    });
    const rewardCount = this.config.actual && this.cart ? rewardModel(this.cart, this.config).count : this.cart?.count ?? 0;
    const active = document.activeElement;
    const editing = active?.matches('input:not([type="hidden"]):not([type="button"]):not([type="submit"]), textarea, select, [contenteditable="true"]');
    const viewport = window.visualViewport;
    const keyboard = viewport && viewport.scale <= 1.05 && innerHeight - viewport.height - viewport.offsetTop > 150;
    const cookie = visibleSurface(document.getElementById('shopify-pc__banner'));
    const chat = document.getElementById('zipchat-iframe')?.dataset.zipchatVisible === 'true';
    const preview = document.getElementById('PBarNextFrame');
    const previewHeight = visibleSurface(preview) ? preview.parentElement?.getBoundingClientRect().height : 0;
    const previewOffset = previewHeight > 20 && previewHeight < 150 ? previewHeight : visibleSurface(preview) ? 64 : 0;
    const offset = `${Math.ceil(previewOffset)}px`;
    if (document.documentElement.style.getPropertyValue('--qr-rewards-bottom-offset') !== offset) document.documentElement.style.setProperty('--qr-rewards-bottom-offset', offset);
    let stickyAllowed = false;
    for (const host of this.widgets.keys()) {
      const context = host.dataset.context;
      const placement = this.config.placement;
      const allowed = context === 'sticky' ? placement !== 'cart' : placement !== 'sticky';
      host.hidden = !this.valid || !rewardCount || !allowed || otherModal || context === 'sticky' && (drawerOpen || pageWidget && placement === 'both') || context === 'drawer' && !drawerOpen;
      if (context === 'sticky') {
        host.hidden ||= Boolean(editing || keyboard || cookie || chat);
        stickyAllowed ||= !host.hidden;
        const ui = this.widgets.get(host);
        host.dataset.collapsed = String(this.stickyCollapsed);
        ui.card.hidden = this.stickyCollapsed;
        if (ui.reopen) ui.reopen.hidden = !this.stickyCollapsed;
        if (ui.minimize) ui.minimize.setAttribute('aria-expanded', String(!this.stickyCollapsed));
        host.hidden ||= this.mobile.matches && this.stickyCollapsed && this.purchaseEntryVisible;
      }
      if (host.hidden && this.celebratingHost === host) this.clearCelebration();
      if (host.hidden && this.attentionHost === host) this.clearAttention();
    }
    this.syncFloatingSpace(stickyAllowed);
    this.showFirstAttention();
  }

  showFirstAttention() {
    if (!this.attentionObserver || !this.config.actual || !this.valid || this.updating || !this.cart || document.hidden || window.Shopify?.designMode) return;
    const model = rewardModel(this.cart, this.config);
    if (model.count < 1 || !model.next) return;
    const host = [...this.widgets.keys()].filter(node => node.isConnected && !node.hidden && !(node.dataset.context === 'sticky' && this.stickyCollapsed) && (this.attentionIntersections.get(node) ?? 0) >= .25).sort((a, b) => Number(b.dataset.context === 'drawer') - Number(a.dataset.context === 'drawer'))[0];
    const ui = this.widgets.get(host);
    if (!ui || ui.headline.dataset.copy !== 'goal' || !claimFirstAttention() || this.motion.matches) return;
    this.clearAttention();
    this.attentionHost = host;
    ui.card.classList.add('is-attention');
    ui.badges.get(model.next.index)?.classList.add('is-attention-target');
    this.attentionTimer = setTimeout(this.clearAttention, 700);
  }

  clearAttention = () => {
    clearTimeout(this.attentionTimer);
    for (const ui of this.widgets.values()) {
      ui.card.classList.remove('is-attention');
      for (const badge of ui.badges.values()) badge.classList.remove('is-attention-target');
    }
    this.attentionHost = null;
  };

  onCartEvent = async event => {
    if (!event.promise || typeof event.promise.then !== 'function') return;
    const signal = this.abort.signal;
    this.epoch++;
    this.pending++;
    this.clearCelebration();
    this.clearAttention();
    this.setUpdating(true);
    this.syncVisibility();
    try {
      const result = await event.promise;
      if (signal.aborted) return;
      if (!result || !result.cart || result.detail?.didError || result.userErrors?.length) throw new Error('Cart update failed');
      this.addSucceeded ||= event.type === StandardEvents.cartLinesUpdate && event.action === 'add';
      this.dirty = true;
    } catch {
      if (!signal.aborted) {
        this.addSucceeded = false;
        this.dirty = true;
      }
    } finally {
      if (!signal.aborted) {
        this.pending--;
        if (!this.pending) this.drain();
      }
    }
  };

  requestRefresh(allowCelebration = false) {
    this.clearAttention();
    this.epoch++;
    this.dirty = true;
    if (!allowCelebration) this.addSucceeded = false;
    this.setUpdating(true);
    this.syncVisibility();
    this.drain();
  }

  async drain() {
    if (this.running || this.pending || !this.dirty || this.abort.signal.aborted) return;
    this.running = true;
    try {
      while (this.dirty && !this.pending && !this.abort.signal.aborted) {
        this.dirty = false;
        const epoch = this.epoch;
        const previous = this.cart;
        try {
          const root = sameOriginUrl(this.config.rootUrl, location.href);
          if (!root || !root.pathname.endsWith('/')) throw new Error('Invalid cart root');
          const url = new URL('cart', root);
          url.searchParams.set('section_id', 'qr-quiz-rewards-data');
          const response = await fetch(url, { method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: this.abort.signal, headers: { Accept: 'text/html' } });
          if (!response.ok || new URL(response.url || url.href).origin !== location.origin) throw new Error('Cart data unavailable');
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const cart = normalizeCart(readJson('script[data-qr-rewards-cart]', parsed));
          if (!cart) throw new Error('Invalid cart data');
          if (this.abort.signal.aborted || epoch !== this.epoch || this.pending) continue;
          this.cart = cart;
          this.valid = true;
          const addSucceeded = this.addSucceeded;
          this.addSucceeded = false;
          this.setUpdating(false);
          this.syncDrawerCount();
          for (const host of this.widgets.keys()) this.render(host);
          this.syncVisibility();
          if (previous) this.celebrate(crossedMilestone(previous, cart, this.config, addSucceeded));
        } catch {
          if (epoch === this.epoch && !this.abort.signal.aborted) {
            this.valid = false;
            this.addSucceeded = false;
            this.setUpdating(false);
            this.clearCelebration();
            this.syncVisibility();
          }
        }
      }
    } finally {
      this.running = false;
      if (this.dirty && !this.pending && !this.abort.signal.aborted) this.drain();
    }
  }

  celebrate(tier) {
    if (!tier || this.motion.matches || document.hidden || window.Shopify?.designMode) return;
    const host = [...this.widgets.keys()].filter(node => !node.hidden && !(node.dataset.context === 'sticky' && this.stickyCollapsed)).sort((a, b) => Number(b.dataset.context === 'drawer') - Number(a.dataset.context === 'drawer'))[0];
    const ui = this.widgets.get(host);
    const badge = ui?.badges.get(tier.index);
    if (!ui || !badge) return;
    this.clearCelebration();
    this.celebratingHost = host;
    badge.classList.add('is-unlocking');
    const layer = element('div', 'qr-rewards__celebration');
    layer.setAttribute('aria-hidden', 'true');
    const colors = tier.index === 2 ? ['#eeb844', '#0759c7', '#355b36'] : ['#0759c7', '#35aa22', '#f4f0e6'];
    for (let index = 0; index < (tier.index === 2 ? 16 : 10); index++) {
      const spark = element('i', 'qr-rewards__spark');
      spark.style.setProperty('--spark-x', `${8 + index * 84 / (tier.index === 2 ? 15 : 9)}%`);
      spark.style.setProperty('--spark-dx', `${(index % 2 ? 1 : -1) * (14 + index % 4 * 7)}px`);
      spark.style.setProperty('--spark-rise', `${28 + index % 4 * 12}px`);
      spark.style.setProperty('--spark-turn', `${(index % 2 ? 1 : -1) * (70 + index * 17)}deg`);
      spark.style.setProperty('--spark-delay', `${index % 5 * 35}ms`);
      spark.style.setProperty('--spark-color', colors[index % colors.length]);
      layer.append(spark);
    }
    ui.card.append(layer);
    ui.status.setAttribute('aria-live', 'polite');
    ui.status.textContent = interpolate(this.config.actual ? this.config.labels.actual_announcement : this.config.labels.announcement, tier);
    this.celebrationTimer = setTimeout(this.clearCelebration, tier.index === 2 ? 1900 : 1550);
  }

  clearCelebration = () => {
    clearTimeout(this.celebrationTimer);
    for (const ui of this.widgets.values()) {
      ui.card.querySelector('.qr-rewards__celebration')?.remove();
      for (const badge of ui.badges.values()) badge.classList.remove('is-unlocking');
      ui.status.textContent = '';
      ui.status.setAttribute('aria-live', 'off');
    }
    this.celebratingHost = null;
  };

  destroy() {
    this.abort.abort();
    this.clearCelebration();
    this.clearAttention();
    this.modalObserver.disconnect();
    this.overlayObserver.disconnect();
    this.attentionObserver?.disconnect();
    this.floatingObserver?.disconnect();
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
    this.widgets.clear();
    this.syncFloatingSpace(false);
    document.documentElement.style.removeProperty('--qr-rewards-bottom-offset');
  }
}

let activeController = null;

export class QRQuizRewards extends Component {
  connectedCallback() {
    const config = normalizeConfig(readRuntimeConfig());
    if (!config) {
      this.hidden = true;
      return;
    }
    super.connectedCallback();
    this.rewardsAbort = new AbortController();
    this.addEventListener('click', this.onViewCart, { signal: this.rewardsAbort.signal });
    activeController ??= new RewardsController(config);
    this.rewardsController = activeController;
    activeController.mount(this);
    this.renderObserver = new MutationObserver(() => {
      if (this.isConnected && this.rewardsController && !this.querySelector('.qr-rewards__card')) this.rewardsController.mount(this, true);
    });
    this.renderObserver.observe(this, { childList: true });
  }

  updatedCallback() {
    if (!this.rewardsController) return;
    super.updatedCallback();
    this.rewardsController.mount(this, true);
  }

  disconnectedCallback() {
    this.renderObserver?.disconnect();
    this.rewardsAbort?.abort();
    this.rewardsController?.unmount(this);
    this.rewardsController = null;
    super.disconnectedCallback();
  }

  onViewCart = event => {
    if (!(event.target instanceof Element) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const action = event.target.closest('[data-qr-rewards-keep-shopping], [data-qr-rewards-view-cart], [data-qr-rewards-minimize], [data-qr-rewards-expand]');
    if (!action || !this.contains(action)) return;
    if (action.hasAttribute('data-qr-rewards-minimize') || action.hasAttribute('data-qr-rewards-expand')) {
      event.preventDefault();
      this.rewardsController?.setStickyCollapsed(action.hasAttribute('data-qr-rewards-minimize'), true);
      return;
    }
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (action.hasAttribute('data-qr-rewards-keep-shopping')) {
      if (typeof drawer?.close === 'function') {
        event.preventDefault();
        drawer.close();
      }
      return;
    }
    if (typeof drawer?.open === 'function') {
      event.preventDefault();
      drawer.open();
    }
  };
}

if (typeof document !== 'undefined' && normalizeConfig(readRuntimeConfig()) && !customElements.get('qr-quiz-rewards')) {
  customElements.define('qr-quiz-rewards', QRQuizRewards);
}
